import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { decodeToken } from '../api/common';

const NotificationContext = createContext();

// 모듈별 뱃지 스타일 매핑 함수 (메뉴별 고유 색상 구분)
const getModuleBadgeStyle = (moduleName, moduleType) => {
  const target = moduleName || moduleType;
  switch (target) {
    case '업무협조':
    case '업무지시':
    case 'work_order':
      return { bg: '#2563eb', color: '#fff' }; // 파란색 (업무협조)
    case '프로젝트':
    case 'pms':
      return { bg: '#f57c00', color: '#fff' }; // 주황색 (프로젝트)
    case '전자결재':
    case 'approval':
      return { bg: '#10b981', color: '#fff' }; // 초록색 (전자결재)
    case '일정':
    case 'calendar':
      return { bg: '#8b5cf6', color: '#fff' }; // 보라색 (일정)
    default:
      return { bg: '#64748b', color: '#fff' }; // 슬레이트 (기타)
  }
};

export const useNotification = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [toastInfo, setToastInfo] = useState(null); // ✅ 토스트 상태 추가
  const navigate = useNavigate();

  const handleToastClick = async () => {
    if (!toastInfo) return;
    const token = sessionStorage.getItem('token');
    let feedIdToCheck = null;
    let targetUrl = toastInfo.url;

    // [사전 검사] PMS 원본 피드 존재 여부 확인 (PMS 모듈 전용)
    if (toastInfo.module_type === 'pms' && toastInfo.action_type === 'feed_create') {
      try {
        const extra = typeof toastInfo.extra_data === 'string' ? JSON.parse(toastInfo.extra_data) : (toastInfo.extra_data || {});
        feedIdToCheck = extra.feedId;

        // 이동할 URL 조작 미리 준비
        if (!targetUrl.includes('tab=')) {
          targetUrl += (targetUrl.includes('?') ? '&' : '?') + 'tab=comments';
        }
        if (feedIdToCheck && !targetUrl.includes('focusId=')) {
          targetUrl += `&focusId=${feedIdToCheck}`;
        }
      } catch (e) { }

      // 서버에 원본 존재 여부 확인
      if (feedIdToCheck) {
        try {
          const checkRes = await fetch(`/api/pms/feedbacks/${feedIdToCheck}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (!checkRes.ok && checkRes.status === 404) {
            // 글이 없음(삭제됨)
            setToastInfo(null); // 팝업 닫기
            if (window.confirm('이미 삭제된 게시글입니다.\n이 알림 내역을 삭제하시겠습니까?')) {
              await fetch(`/api/notifications/${toastInfo.id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
              });
              // 안 읽은 상태였다면 카운트 차감
              if (!toastInfo.is_read) {
                decrementUnread();
              }
            }
            return; // 이동 차단
          }
        } catch (err) {
          console.error('원본 글 존재 여부 확인 실패', err);
        }
      }
    }

    // 원본이 존재하거나 검사 대상이 아닌 경우: 정상 진행 (읽음 처리 후 이동)
    try {
      await fetch(`/api/notifications/${toastInfo.id}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!toastInfo.is_read) decrementUnread();
    } catch (err) {
      console.error('토스트 클릭 시 읽음 처리 실패', err);
    }

    setToastInfo(null); // 팝업 닫기
    if (targetUrl) {
      navigate(targetUrl, { state: { notificationTrigger: Date.now() } }); // 피드로 이동
    }
  };

  // 안 읽은 알림 개수 초기 세팅
  const fetchUnreadCount = async () => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) return;
      const res = await fetch('/api/notifications/unread-count', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.unreadCount);
      }
    } catch (err) {
      console.error('안읽은 알림 수 조회 실패', err);
    }
  };

  useEffect(() => {
    let currentSocket = null;

    const connectSocket = () => {
      const token = sessionStorage.getItem('token');
      if (!token) return null;

      const payload = decodeToken(token);
      if (!payload?.id) return null;

      fetchUnreadCount();

      // 소켓 연결 (.env에 설정된 URL이 있으면 우선 사용, 없으면 운영 환경은 현재 도메인, 로컬은 5000번 포트)
      const socketUrl = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:5000');
      const newSocket = io(socketUrl, {
        transports: ['websocket', 'polling']
      });

      newSocket.on('connect', () => {
        newSocket.emit('join', payload.id); // 내 고유 ID로 방 입장
      });

      newSocket.on('notification', (noti) => {
        setUnreadCount(prev => prev + 1); // 실시간 뱃지 증가

        // 실시간 토스트 팝업 표시 (5초 후 자동 닫힘)
        setToastInfo(noti);
        setTimeout(() => setToastInfo(null), 5000);
      });

      setSocket(newSocket);
      return newSocket;
    };

    // 초기 연결 시도
    currentSocket = connectSocket();

    // 로그인 직후(새로고침 전)에도 소켓이 연결될 수 있도록 1초마다 토큰 확인
    const interval = setInterval(() => {
      if (!currentSocket && sessionStorage.getItem('token')) {
        currentSocket = connectSocket();
      } else if (currentSocket && !sessionStorage.getItem('token')) {
        // 로그아웃 시 소켓 해제
        currentSocket.disconnect();
        currentSocket = null;
        setSocket(null);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
      if (currentSocket) {
        currentSocket.disconnect();
      }
    };
  }, []);

  const decrementUnread = () => {
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllReadContext = () => {
    setUnreadCount(0);
  };

  return (
    <NotificationContext.Provider value={{ socket, unreadCount, decrementUnread, markAllReadContext, fetchUnreadCount }}>
      {children}

      {/* _BO와 동일한 전역 토스트 UI (다크 테마) */}
      {toastInfo && (
        <div style={{
          position: 'fixed', bottom: '40px', right: '20px', zIndex: 99999,
          backgroundColor: '#323232', color: '#fff', padding: '10px 12px', paddingRight: '32px',
          borderRadius: '8px', fontSize: '12px', fontWeight: 'bold',
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
          border: '1px solid #666', // 테두리 추가
          animation: 'fadeInUp 0.3s ease',
          display: 'flex', alignItems: 'flex-start', gap: '8px',
          minWidth: '260px', maxWidth: '320px',
          lineHeight: '1.4', wordBreak: 'keep-all',
          cursor: 'pointer'
        }}
          onClick={handleToastClick}
        >
          <i className="fas fa-bell" style={{ color: '#ffc107', fontSize: '14px', marginTop: '2px' }}></i>
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: '4px', overflow: 'hidden' }}>
            {toastInfo.extra_data && (toastInfo.extra_data.moduleName || toastInfo.extra_data.projectName || toastInfo.extra_data.orderTitle) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {toastInfo.extra_data.moduleName && (() => {
                  const badgeStyle = getModuleBadgeStyle(toastInfo.extra_data.moduleName, toastInfo.module_type);
                  return (
                    <span style={{
                      backgroundColor: badgeStyle.bg, color: badgeStyle.color,
                      padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold'
                    }}>
                      {toastInfo.extra_data.moduleName}
                    </span>
                  );
                })()}
                {(toastInfo.extra_data.projectName || toastInfo.extra_data.orderTitle) && (
                  <span style={{
                    backgroundColor: '#e6f0ff', color: '#3ab0ff',
                    padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold'
                  }}>
                    {toastInfo.extra_data.projectName || toastInfo.extra_data.orderTitle}
                  </span>
                )}
              </div>
            )}
            <div
              style={{
                fontSize: '11px', color: '#fff', fontWeight: 'bold', marginTop: '1px',
                maxHeight: '4.2em', // 1.4(lineHeight) * 3줄 = 4.2em
                overflowY: 'auto',  // 4줄 이상일 경우 스크롤바 생성
                paddingRight: '4px'
              }}
            >
              {toastInfo.message}
            </div>
          </div>
          <i
            className="fas fa-times"
            style={{
              position: 'absolute', top: '8px', right: '8px', padding: '4px',
              cursor: 'pointer', color: '#999', fontSize: '12px',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.color = '#fff'}
            onMouseLeave={(e) => e.target.style.color = '#999'}
            onClick={(e) => {
              e.stopPropagation(); // 팝업 전체 클릭(페이지 이동) 이벤트 방지
              setToastInfo(null);
            }}
            title="닫기"
          ></i>
        </div>
      )}
    </NotificationContext.Provider>
  );
};
